import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  EditOutlined,
  MessageOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RobotOutlined,
  SaveOutlined,
  SendOutlined,
  SettingOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  message,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_AI_FALLBACK_MESSAGE,
  DEFAULT_AI_SYSTEM_PROMPT,
  DEFAULT_AI_WELCOME_MESSAGE,
} from '../config/aiDefaults';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface AIConfig {
  provider: 'openai' | 'claude' | 'demo';
  openaiApiKey?: string;
  claudeApiKey?: string;
  model: string;
  temperature: number;
  maxTokens: number;
  systemPrompt: string;
  welcomeMessage: string;
  fallbackMessage: string;
  marketingMessage: string;
  marketingFrequency: number;
  enabledBots: {
    telegram: boolean;
    whatsapp: boolean;
    viber: boolean;
  };
  vectorStore: {
    enabled: boolean;
    provider: string;
    apiKey?: string;
    url?: string;
  };
  knowledgeBase: {
    autoUpdate: boolean;
    updateFrequency: string;
  };
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  isActive: boolean;
}

const AISettings: React.FC = () => {
  const [vectorStoreStatus, setVectorStoreStatus] = useState<
    'connected' | 'disconnected' | 'checking' | null
  >(null);
  const [aiConnectionStatus, setAiConnectionStatus] = useState<
    'connected' | 'disconnected' | 'checking' | null
  >(null);
  const [config, setConfig] = useState<AIConfig>({
    provider: 'demo',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    maxTokens: 500,
    systemPrompt: DEFAULT_AI_SYSTEM_PROMPT,
    welcomeMessage: DEFAULT_AI_WELCOME_MESSAGE,
    fallbackMessage: DEFAULT_AI_FALLBACK_MESSAGE,
    marketingMessage: '',
    marketingFrequency: 5,
    enabledBots: {
      telegram: true,
      whatsapp: false,
      viber: false,
    },
    vectorStore: {
      enabled: false,
      provider: 'pinecone',
      apiKey: '',
      url: '',
    },
    knowledgeBase: {
      autoUpdate: true,
      updateFrequency: 'daily',
    },
  });

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [form] = Form.useForm();
  const [faqForm] = Form.useForm();
  const { t } = useTranslation();
  const { user } = useAuthStore();

  useEffect(() => {
    fetchConfig();
    fetchFAQs();
    testAIConnection();
  }, []);

  const testAIConnection = async () => {
    setAiConnectionStatus('checking');
    try {
      // First, get the current provider info
      const providerResponse = await api.get('/chat/ai/provider');
      const providerData = providerResponse.data;

      // Check if the current provider has necessary API keys
      if (providerData.provider === 'demo') {
        setAiConnectionStatus('connected');
        return;
      }

      // For non-demo providers, check if API key is configured
      if (providerData.provider === 'openai' && !providerData.available?.openai) {
        setAiConnectionStatus('disconnected');
        return;
      }

      if (providerData.provider === 'claude' && !providerData.available?.claude) {
        setAiConnectionStatus('disconnected');
        return;
      }

      // Try to switch to the provider to verify it's working
      const switchResponse = await api.post('/chat/ai/provider', {
        provider: providerData.provider,
      });

      if (switchResponse.data.success) {
        setAiConnectionStatus('connected');
      } else {
        setAiConnectionStatus('disconnected');
      }
    } catch (error) {
      console.error('Failed to test AI connection:', error);
      setAiConnectionStatus('disconnected');
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await api.get('/chat/ai/config');
      const configData = response.data;

      setConfig({
        ...config,
        ...configData,
        marketingMessage: configData.marketingMessage || '',
        marketingFrequency: configData.marketingFrequency || 5,
        enabledBots: {
          telegram: configData.telegramEnabled,
          whatsapp: configData.whatsappEnabled,
          viber: configData.viberEnabled,
        },
        vectorStore: {
          enabled: configData.vectorStoreEnabled,
          provider: configData.vectorStoreProvider || 'pinecone',
          apiKey: configData.vectorStoreApiKey || '',
          url: configData.vectorStoreUrl || '',
        },
        knowledgeBase: {
          autoUpdate: configData.autoUpdateKnowledge ?? true,
          updateFrequency: configData.updateFrequency || 'daily',
        },
      });

      form.setFieldsValue({
        ...configData,
        marketingMessage: configData.marketingMessage || '',
        marketingFrequency: configData.marketingFrequency || 5,
        enabledBots: {
          telegram: configData.telegramEnabled,
          whatsapp: configData.whatsappEnabled,
          viber: configData.viberEnabled,
        },
      });
    } catch (error) {
      console.error('Failed to fetch AI config:', error);
    }
  };

  const fetchFAQs = async () => {
    try {
      const response = await api.get('/faqs');
      setFaqs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    }
  };

  const handleSaveConfig = async (values: any) => {
    setLoading(true);
    try {
      // Quick debug to see if function is called
      const hasMarketing = values.marketingMessage && values.marketingMessage.trim();
      if (hasMarketing) {
        message.info(
          t('aiSettings.marketingSaving', { preview: values.marketingMessage.substring(0, 50) })
        );
      }

      const hasActiveConnection = aiConnectionStatus === 'connected';
      const isCurrentProvider = config?.provider === values.provider;

      // Transform the values to match backend format
      const payload = {
        provider: values.provider,
        // Only include API keys if provided OR if no active connection for this provider
        openaiApiKey:
          values.openaiApiKey ||
          (hasActiveConnection && isCurrentProvider && values.provider === 'openai'
            ? undefined
            : ''),
        claudeApiKey:
          values.claudeApiKey ||
          (hasActiveConnection && isCurrentProvider && values.provider === 'claude'
            ? undefined
            : ''),
        model: values.model,
        temperature: values.temperature,
        maxTokens: values.maxTokens,
        systemPrompt: values.systemPrompt,
        welcomeMessage: values.welcomeMessage,
        fallbackMessage: values.fallbackMessage,
        marketingMessage: values.marketingMessage || '',
        marketingFrequency: values.marketingFrequency || 5,
        telegramEnabled: values.enabledBots?.telegram,
        whatsappEnabled: values.enabledBots?.whatsapp,
        viberEnabled: values.enabledBots?.viber,
        vectorStoreEnabled: config?.vectorStore?.enabled,
        vectorStoreProvider: config?.vectorStore?.provider || 'pinecone',
        vectorStoreApiKey: config?.vectorStore?.apiKey || '',
        vectorStoreUrl: config?.vectorStore?.url || '',
        autoUpdateKnowledge: config?.knowledgeBase?.autoUpdate ?? true,
        updateFrequency: config?.knowledgeBase?.updateFrequency || 'daily',
      };

      // Remove undefined API keys from payload to avoid overwriting existing ones
      if (payload.openaiApiKey === undefined) {
        delete payload.openaiApiKey;
      }
      if (payload.claudeApiKey === undefined) {
        delete payload.claudeApiKey;
      }

      await api.post('/chat/ai/config', payload);
      message.success(t('aiSettings.configSaved'));

      // Update the config state with all values
      setConfig({
        ...values,
        vectorStore: config?.vectorStore,
        knowledgeBase: config?.knowledgeBase,
      });

      // Retest connection after saving
      await testAIConnection();
    } catch (error) {
      message.error(t('aiSettings.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setAiConnectionStatus('checking');
    try {
      const provider = form.getFieldValue('provider');
      const apiKey =
        provider === 'openai'
          ? form.getFieldValue('openaiApiKey')
          : form.getFieldValue('claudeApiKey');

      // For demo provider, always success
      if (provider === 'demo') {
        setAiConnectionStatus('connected');
        message.success(t('aiSettings.demoConnectionOk'));
        setLoading(false);
        return;
      }

      // Test by switching provider
      const response = await api.post('/chat/ai/provider', { provider });

      if (response.data.success) {
        setAiConnectionStatus('connected');
        message.success(t('aiSettings.connectionOk'));
      } else {
        setAiConnectionStatus('disconnected');
        message.error(t('aiSettings.connectionFailedCheckKey'));
      }
    } catch (error) {
      setAiConnectionStatus('disconnected');
      message.error(t('aiSettings.connectionFailedCheckKey'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFAQ = () => {
    setEditingFaq(null);
    faqForm.resetFields();
    setModalVisible(true);
  };

  const handleEditFAQ = (faq: FAQ) => {
    setEditingFaq(faq);
    faqForm.setFieldsValue(faq);
    setModalVisible(true);
  };

  const handleDeleteFAQ = async (id: string) => {
    try {
      await api.delete(`/faqs/${id}`);
      message.success(t('aiSettings.faqDeleted'));
      fetchFAQs();
    } catch (error) {
      message.error(t('aiSettings.deleteFailed'));
    }
  };

  const handleSubmitFAQ = async (values: any) => {
    try {
      if (editingFaq) {
        await api.patch(`/faqs/${editingFaq.id}`, values);
        message.success(t('aiSettings.faqUpdated'));
      } else {
        await api.post('/faqs', values);
        message.success(t('aiSettings.faqCreated'));
      }
      setModalVisible(false);
      fetchFAQs();
    } catch (error) {
      message.error(t('aiSettings.operationFailed'));
    }
  };

  const modelOptions = {
    demo: [{ value: 'demo', label: t('aiSettings.modelDemo') }],
    openai: [
      { value: 'gpt-4-turbo-preview', label: t('aiSettings.modelGpt4Turbo') },
      { value: 'gpt-4', label: t('aiSettings.modelGpt4') },
      { value: 'gpt-3.5-turbo', label: t('aiSettings.modelGpt35Turbo') },
    ],
    claude: [
      { value: 'claude-3-opus', label: t('aiSettings.modelClaudeOpus') },
      { value: 'claude-3-sonnet', label: t('aiSettings.modelClaudeSonnet') },
      { value: 'claude-3-haiku', label: t('aiSettings.modelClaudeHaiku') },
    ],
  };

  const faqColumns = [
    {
      title: t('aiSettings.columnQuestion'),
      dataIndex: 'question',
      key: 'question',
      width: '40%',
    },
    {
      title: t('aiSettings.columnAnswer'),
      dataIndex: 'answer',
      key: 'answer',
      width: '35%',
      render: (text: string) => <Paragraph ellipsis={{ rows: 2 }}>{text}</Paragraph>,
    },
    {
      title: t('aiSettings.columnCategory'),
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: t('aiSettings.columnStatus'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? t('aiSettings.statusActive') : t('aiSettings.statusInactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: FAQ) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditFAQ(record)} />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteFAQ(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <RobotOutlined /> {t('aiSettings.pageTitle')}
          </Title>
        </Col>
        <Col>
          <Space>
            {aiConnectionStatus === 'connected' && (
              <Badge
                status="success"
                text={
                  <Space>
                    <WifiOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ color: '#52c41a' }}>{t('aiSettings.connOn')}</Text>
                  </Space>
                }
              />
            )}
            {aiConnectionStatus === 'disconnected' && (
              <Badge
                status="error"
                text={
                  <Space>
                    <DisconnectOutlined style={{ color: '#f5222d' }} />
                    <Text style={{ color: '#f5222d' }}>{t('aiSettings.connOff')}</Text>
                  </Space>
                }
              />
            )}
            {aiConnectionStatus === 'checking' && (
              <Badge
                status="processing"
                text={
                  <Space>
                    <SyncOutlined spin style={{ color: '#1890ff' }} />
                    <Text style={{ color: '#1890ff' }}>{t('aiSettings.connChecking')}</Text>
                  </Space>
                }
              />
            )}
            <Button icon={<ReloadOutlined />} onClick={testAIConnection} size="small">
              {t('aiSettings.recheck')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs defaultActiveKey="general">
        <TabPane tab={t('aiSettings.tabGeneral')} key="general">
          <Form form={form} layout="vertical" onFinish={handleSaveConfig} initialValues={config}>
            <Card
              title={
                <Space>
                  <span>{t('aiSettings.providerCardTitle')}</span>
                  {aiConnectionStatus === 'connected' && (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      {t('aiSettings.tagConnOk')}
                    </Tag>
                  )}
                  {aiConnectionStatus === 'disconnected' && (
                    <Tag color="error" icon={<CloseCircleOutlined />}>
                      {t('aiSettings.tagNoConn')}
                    </Tag>
                  )}
                  {aiConnectionStatus === 'checking' && (
                    <Tag color="processing" icon={<SyncOutlined spin />}>
                      {t('aiSettings.tagChecking')}
                    </Tag>
                  )}
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="provider"
                    label={t('aiSettings.labelProvider')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group>
                      <Radio.Button value="demo">
                        <Space>
                          <ThunderboltOutlined />
                          {t('aiSettings.providerDemo')}
                        </Space>
                      </Radio.Button>
                      <Radio.Button value="openai">
                        <Space>
                          <ApiOutlined />
                          {t('aiSettings.providerOpenai')}
                        </Space>
                      </Radio.Button>
                      <Radio.Button value="claude">
                        <Space>
                          <RobotOutlined />
                          {t('aiSettings.providerClaude')}
                        </Space>
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.provider !== currentValues.provider
                    }
                  >
                    {({ getFieldValue }) => {
                      const provider = getFieldValue('provider');
                      return (
                        <Form.Item
                          name="model"
                          label={t('aiSettings.labelModel')}
                          rules={[{ required: true }]}
                        >
                          <Select placeholder={t('aiSettings.selectModelPlaceholder')}>
                            {modelOptions[provider as keyof typeof modelOptions]?.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.provider !== currentValues.provider
                    }
                  >
                    {({ getFieldValue }) => {
                      const provider = getFieldValue('provider');
                      const hasActiveConnection = aiConnectionStatus === 'connected';
                      const isCurrentProvider = config?.provider === provider;

                      // Only require API key if:
                      // 1. No active connection, OR
                      // 2. Switching to a different provider
                      const requireApiKey = !(hasActiveConnection && isCurrentProvider);

                      return provider === 'openai' ? (
                        <Form.Item
                          name="openaiApiKey"
                          label={t('aiSettings.labelOpenaiKey')}
                          rules={[{ required: false }]}
                          help={
                            hasActiveConnection && isCurrentProvider
                              ? t('aiSettings.keyOptionalHelp')
                              : t('aiSettings.openaiKeyHelp')
                          }
                        >
                          <Input.Password
                            placeholder={
                              hasActiveConnection && isCurrentProvider
                                ? t('aiSettings.existingKeyPlaceholder')
                                : 'sk-...'
                            }
                            addonAfter={
                              <Button size="small" onClick={handleTestConnection} loading={loading}>
                                {t('aiSettings.testButton')}
                              </Button>
                            }
                          />
                        </Form.Item>
                      ) : provider === 'claude' ? (
                        <Form.Item
                          name="claudeApiKey"
                          label={t('aiSettings.labelClaudeKey')}
                          rules={[{ required: false }]}
                          help={
                            hasActiveConnection && isCurrentProvider
                              ? t('aiSettings.keyOptionalHelp')
                              : t('aiSettings.claudeKeyHelp')
                          }
                        >
                          <Input.Password
                            placeholder={
                              hasActiveConnection && isCurrentProvider
                                ? t('aiSettings.existingKeyPlaceholder')
                                : 'sk-ant-...'
                            }
                            addonAfter={
                              <Button size="small" onClick={handleTestConnection} loading={loading}>
                                {t('aiSettings.testButton')}
                              </Button>
                            }
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="temperature"
                    label={t('aiSettings.labelTemperature')}
                    tooltip={t('aiSettings.temperatureTooltip')}
                  >
                    <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="maxTokens"
                    label={t('aiSettings.labelMaxTokens')}
                    tooltip={t('aiSettings.maxTokensTooltip')}
                  >
                    <InputNumber min={100} max={4000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title={t('aiSettings.promptCardTitle')} style={{ marginBottom: 24 }}>
              <Form.Item
                name="systemPrompt"
                label={t('aiSettings.labelSystemPrompt')}
                tooltip={t('aiSettings.systemPromptTooltip')}
              >
                <TextArea rows={6} placeholder={t('aiSettings.systemPromptPlaceholder')} />
              </Form.Item>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item name="welcomeMessage" label={t('aiSettings.labelWelcome')}>
                    <TextArea rows={3} placeholder={t('aiSettings.welcomePlaceholder')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="fallbackMessage" label={t('aiSettings.labelFallback')}>
                    <TextArea rows={3} placeholder={t('aiSettings.fallbackPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={18}>
                  <Form.Item
                    name="marketingMessage"
                    label={t('aiSettings.labelMarketing')}
                    tooltip={t('aiSettings.marketingTooltip')}
                  >
                    <TextArea rows={3} placeholder={t('aiSettings.marketingPlaceholder')} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="marketingFrequency"
                    label={t('aiSettings.labelFrequency')}
                    tooltip={t('aiSettings.frequencyTooltip')}
                  >
                    <InputNumber min={3} max={20} style={{ width: '100%' }} placeholder="5" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title={t('aiSettings.botsCardTitle')} style={{ marginBottom: 24 }}>
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item name={['enabledBots', 'telegram']} valuePropName="checked">
                    <Space>
                      <Switch />
                      <Text>{t('aiSettings.telegramBot')}</Text>
                    </Space>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['enabledBots', 'whatsapp']} valuePropName="checked">
                    <Space>
                      <Switch />
                      <Text>{t('aiSettings.whatsappBot')}</Text>
                    </Space>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['enabledBots', 'viber']} valuePropName="checked">
                    <Space>
                      <Switch />
                      <Text>{t('aiSettings.viberBot')}</Text>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                  {t('aiSettings.saveSettings')}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={fetchConfig}>
                  {t('aiSettings.reset')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane tab={t('aiSettings.tabKnowledge')} key="knowledge">
          <Card
            title={
              <Space>
                <span>{t('aiSettings.vectorCardTitle')}</span>
                {vectorStoreStatus === 'connected' && (
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    {t('aiSettings.tagConnected')}
                  </Tag>
                )}
                {vectorStoreStatus === 'disconnected' && (
                  <Tag color="red" icon={<CloseCircleOutlined />}>
                    {t('aiSettings.tagNoConn')}
                  </Tag>
                )}
                {vectorStoreStatus === 'checking' && (
                  <Tag color="blue" icon={<SyncOutlined spin />}>
                    {t('aiSettings.tagChecking')}
                  </Tag>
                )}
              </Space>
            }
            style={{ marginBottom: 24 }}
            extra={<Alert message={t('aiSettings.vectorAlert')} type="info" showIcon />}
          >
            <Form layout="vertical">
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item label={t('aiSettings.labelVectorStore')}>
                    <Space>
                      <Switch
                        checked={config?.vectorStore?.enabled}
                        onChange={(checked) =>
                          setConfig({
                            ...config,
                            vectorStore: {
                              ...config?.vectorStore,
                              enabled: checked,
                              provider: config?.vectorStore?.provider || 'pinecone',
                              apiKey: config?.vectorStore?.apiKey || '',
                              url: config?.vectorStore?.url || '',
                            },
                          })
                        }
                      />
                      <Text>{t('aiSettings.enabled')}</Text>
                    </Space>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={t('aiSettings.labelProviderPlain')}>
                    <Select
                      value={config?.vectorStore?.provider || 'pinecone'}
                      onChange={(value) =>
                        setConfig({
                          ...config,
                          vectorStore: { ...config?.vectorStore, provider: value },
                        })
                      }
                    >
                      <Option value="pinecone">{t('aiSettings.vectorPinecone')}</Option>
                      <Option value="weaviate">{t('aiSettings.vectorWeaviate')}</Option>
                      <Option value="qdrant">{t('aiSettings.vectorQdrant')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={t('aiSettings.labelApiKey')}>
                    <Input.Password
                      placeholder={t('aiSettings.vectorApiKeyPlaceholder')}
                      value={config?.vectorStore?.apiKey || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          vectorStore: { ...config?.vectorStore, apiKey: e.target.value },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={16}>
                  <Form.Item label={t('aiSettings.labelVectorUrl')}>
                    <Input
                      placeholder={t('aiSettings.vectorUrlPlaceholder')}
                      value={config?.vectorStore?.url || ''}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          vectorStore: { ...config?.vectorStore, url: e.target.value },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label=" ">
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={async () => {
                        if (!config?.vectorStore?.url) {
                          message.warning(t('aiSettings.vectorStoreUrlRequired'));
                          return;
                        }
                        setLoading(true);
                        setVectorStoreStatus('checking');
                        try {
                          // Use backend endpoint to test connection
                          const response = await api.post('/chat/ai/test-vector-store', {
                            url: config.vectorStore.url,
                            provider: config.vectorStore.provider || 'qdrant',
                            apiKey: config.vectorStore.apiKey || '',
                          });

                          if (response.data.success) {
                            message.success(
                              response.data.message || t('aiSettings.vectorConnectionOk')
                            );
                            setVectorStoreStatus('connected');
                          } else {
                            message.error(
                              response.data.message || t('aiSettings.vectorConnectionFailed')
                            );
                            setVectorStoreStatus('disconnected');
                          }
                        } catch (error: any) {
                          const errorMessage =
                            error.response?.data?.message ||
                            error.message ||
                            t('aiSettings.unknownError');
                          message.error(
                            t('aiSettings.vectorConnectionFailedReason', { reason: errorMessage })
                          );
                          setVectorStoreStatus('disconnected');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      loading={loading}
                    >
                      {t('aiSettings.testConnection')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card title={t('aiSettings.autoUpdateCardTitle')} style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label={t('aiSettings.labelAutoUpdate')}>
                  <Space>
                    <Switch
                      checked={config?.knowledgeBase?.autoUpdate ?? true}
                      onChange={(checked) =>
                        setConfig({
                          ...config,
                          knowledgeBase: { ...config?.knowledgeBase, autoUpdate: checked },
                        })
                      }
                    />
                    <Text>{t('aiSettings.enabled')}</Text>
                  </Space>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('aiSettings.labelUpdateFrequency')}>
                  <Select
                    value={config?.knowledgeBase?.updateFrequency || 'daily'}
                    onChange={(value) =>
                      setConfig({
                        ...config,
                        knowledgeBase: { ...config?.knowledgeBase, updateFrequency: value },
                      })
                    }
                  >
                    <Option value="hourly">{t('aiSettings.freqHourly')}</Option>
                    <Option value="daily">{t('aiSettings.freqDaily')}</Option>
                    <Option value="weekly">{t('aiSettings.freqWeekly')}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={t('aiSettings.documentsCardTitle')}
            extra={
              <Button type="primary" icon={<PlusOutlined />}>
                {t('aiSettings.uploadDocument')}
              </Button>
            }
          >
            <Alert
              message={t('aiSettings.documentsAlert')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <List
              dataSource={[]}
              renderItem={(item: any) => <List.Item>{item}</List.Item>}
              locale={{ emptyText: t('aiSettings.noDocuments') }}
            />
          </Card>

          <Card style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    // Get current form values for the general settings
                    const formValues = form.getFieldsValue();
                    const hasActiveConnection = aiConnectionStatus === 'connected';
                    const isCurrentProvider = config?.provider === formValues.provider;

                    const payload = {
                      // Include general settings from form
                      provider: formValues.provider || config?.provider || 'demo',
                      // Only include API keys if provided OR if no active connection for this provider
                      openaiApiKey:
                        formValues.openaiApiKey ||
                        (hasActiveConnection &&
                        isCurrentProvider &&
                        formValues.provider === 'openai'
                          ? undefined
                          : ''),
                      claudeApiKey:
                        formValues.claudeApiKey ||
                        (hasActiveConnection &&
                        isCurrentProvider &&
                        formValues.provider === 'claude'
                          ? undefined
                          : ''),
                      model: formValues.model || config?.model || 'demo',
                      temperature: formValues.temperature || config?.temperature || 0.7,
                      maxTokens: formValues.maxTokens || config?.maxTokens || 500,
                      systemPrompt: formValues.systemPrompt || config?.systemPrompt || '',
                      welcomeMessage: formValues.welcomeMessage || config?.welcomeMessage || '',
                      fallbackMessage: formValues.fallbackMessage || config?.fallbackMessage || '',
                      marketingMessage:
                        formValues.marketingMessage || config?.marketingMessage || '',
                      marketingFrequency:
                        formValues.marketingFrequency || config?.marketingFrequency || 5,
                      telegramEnabled:
                        formValues.enabledBots?.telegram || config?.enabledBots?.telegram,
                      whatsappEnabled:
                        formValues.enabledBots?.whatsapp || config?.enabledBots?.whatsapp,
                      viberEnabled: formValues.enabledBots?.viber || config?.enabledBots?.viber,
                      // Include vector store settings
                      vectorStoreEnabled: config?.vectorStore?.enabled,
                      vectorStoreProvider: config?.vectorStore?.provider || 'pinecone',
                      vectorStoreApiKey: config?.vectorStore?.apiKey || '',
                      vectorStoreUrl: config?.vectorStore?.url || '',
                      // Include knowledge base settings
                      autoUpdateKnowledge: config?.knowledgeBase?.autoUpdate ?? true,
                      updateFrequency: config?.knowledgeBase?.updateFrequency || 'daily',
                    };

                    // Remove undefined API keys from payload to avoid overwriting existing ones
                    if (payload.openaiApiKey === undefined) {
                      delete payload.openaiApiKey;
                    }
                    if (payload.claudeApiKey === undefined) {
                      delete payload.claudeApiKey;
                    }

                    await api.post('/chat/ai/config', payload);
                    message.success(t('aiSettings.knowledgeSaved'));
                  } catch (error) {
                    message.error(t('aiSettings.saveFailed'));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {t('aiSettings.saveSettings')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchConfig}>
                {t('aiSettings.reset')}
              </Button>
            </Space>
          </Card>
        </TabPane>

        <TabPane tab={t('aiSettings.tabFaq')} key="faq">
          <Alert
            message={t('aiSettings.faqAlertMessage')}
            description={t('aiSettings.faqAlertDescription')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Card
            title={t('aiSettings.faqCardTitle')}
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateFAQ}>
                {t('aiSettings.newFaq')}
              </Button>
            }
          >
            <Table
              columns={faqColumns}
              dataSource={faqs}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showTotal: (total) => t('aiSettings.faqPaginationTotal', { count: total }),
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab={t('aiSettings.tabTest')} key="test">
          <TestChatPanel config={config} />
        </TabPane>
      </Tabs>

      <Modal
        title={editingFaq ? t('aiSettings.faqEditTitle') : t('aiSettings.faqNewTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={faqForm} layout="vertical" onFinish={handleSubmitFAQ}>
          <Form.Item
            name="question"
            label={t('aiSettings.columnQuestion')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Input placeholder={t('aiSettings.questionPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="answer"
            label={t('aiSettings.columnAnswer')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <TextArea rows={4} placeholder={t('aiSettings.answerPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label={t('aiSettings.columnCategory')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Select placeholder={t('aiSettings.selectCategoryPlaceholder')}>
                  <Option value="general">{t('aiSettings.catGeneral')}</Option>
                  <Option value="registration">{t('aiSettings.catRegistration')}</Option>
                  <Option value="tickets">{t('aiSettings.catTickets')}</Option>
                  <Option value="location">{t('aiSettings.catLocation')}</Option>
                  <Option value="program">{t('aiSettings.catProgram')}</Option>
                  <Option value="other">{t('aiSettings.catOther')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label={t('aiSettings.columnStatus')}
                valuePropName="checked"
                initialValue={true}
              >
                <Select>
                  <Option value={true}>{t('aiSettings.statusActive')}</Option>
                  <Option value={false}>{t('aiSettings.statusInactive')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingFaq ? t('aiSettings.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('aiSettings.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Test Chat Panel Component
const TestChatPanel: React.FC<{ config: AIConfig }> = ({ config }) => {
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      const eventData = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setEvents(eventData);
      if (eventData.length > 0) {
        setSelectedEvent(eventData[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents([]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendTestMessage = async () => {
    if (!(inputMessage.trim() && selectedEvent)) return;

    const userMessage = { role: 'user', content: inputMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setTestLoading(true);

    try {
      const response = await api.post('/chat/message', {
        message: inputMessage,
        eventId: selectedEvent,
        platform: 'WEB',
      });

      const botMessage = {
        role: 'assistant',
        content: response.data.response || response.data.answer || t('aiSettings.noAnswer'),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content:
          t('aiSettings.errorPrefix') + (error as any).response?.data?.message ||
          t('aiSettings.unknownError'),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setTestLoading(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return (
    <Card title={t('aiSettings.testCardTitle')}>
      <Alert
        message={t('aiSettings.testAlert')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={16}>
          <Select
            style={{ width: '100%' }}
            placeholder={t('aiSettings.selectEventPlaceholder')}
            value={selectedEvent}
            onChange={setSelectedEvent}
          >
            {events.map((event) => (
              <Option key={event.id} value={event.id}>
                {event.title}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={8}>
          <Button onClick={clearMessages} icon={<DeleteOutlined />}>
            {t('aiSettings.clearChat')}
          </Button>
        </Col>
      </Row>

      <div
        style={{
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          height: 400,
          display: 'flex',
          flexDirection: 'column',
          background: '#fafafa',
        }}
      >
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
          }}
        >
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', marginTop: 100 }}>
              <MessageOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div>{t('aiSettings.emptyChat')}</div>
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      background: msg.role === 'user' ? '#1890ff' : '#f0f0f0',
                      color: msg.role === 'user' ? 'white' : 'black',
                      padding: '8px 16px',
                      borderRadius: 16,
                      maxWidth: '70%',
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {testLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div
                    style={{
                      background: '#f0f0f0',
                      padding: '8px 16px',
                      borderRadius: 16,
                    }}
                  >
                    <Space>
                      <div className="ant-spin-dot">
                        <span className="ant-spin-dot-item" />
                        <span className="ant-spin-dot-item" />
                        <span className="ant-spin-dot-item" />
                        <span className="ant-spin-dot-item" />
                      </div>
                      <Text type="secondary">{t('aiSettings.generating')}</Text>
                    </Space>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </Space>
          )}
        </div>

        <div
          style={{
            borderTop: '1px solid #e8e8e8',
            padding: 16,
            background: 'white',
          }}
        >
          <Input.Search
            placeholder={t('aiSettings.inputPlaceholder')}
            enterButton={<SendOutlined />}
            size="large"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onSearch={sendTestMessage}
            loading={testLoading}
            disabled={!selectedEvent}
          />
          <div style={{ marginTop: 8 }}>
            <Space wrap>
              <Button
                size="small"
                onClick={() => setInputMessage(t('aiSettings.quickProgramValue'))}
              >
                {t('aiSettings.quickProgramLabel')}
              </Button>
              <Button
                size="small"
                onClick={() => setInputMessage(t('aiSettings.quickSpeakersValue'))}
              >
                {t('aiSettings.quickSpeakersLabel')}
              </Button>
              <Button size="small" onClick={() => setInputMessage(t('aiSettings.quickVenueValue'))}>
                {t('aiSettings.quickVenueLabel')}
              </Button>
              <Button size="small" onClick={() => setInputMessage(t('aiSettings.quickStartValue'))}>
                {t('aiSettings.quickStartLabel')}
              </Button>
            </Space>
          </div>
        </div>
      </div>

      <Alert
        message={t('aiSettings.providerAlert', {
          provider: config.provider?.toUpperCase() || 'DEMO',
          model: config.model || t('aiSettings.defaultModel'),
        })}
        type={config.provider === 'demo' ? 'warning' : 'success'}
        style={{ marginTop: 16 }}
      />
    </Card>
  );
};

export default AISettings;
